import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSpinner } from "components/Shared";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import StarRatings from "react-star-ratings";
import { isSatisfactionSurveyAvailable, setSatisfactionSurvey } from "requests/satisfactionSurvey/customer";
import responseHandler from "responseHandler";
import { CustomerSatisfactionSurveyRequest } from "interfaces/Mueller.Business.Models";

interface Props {
  match: { params: { uId: string } },
  makeToast: (arg: string) => void,
}

let STATUS = {
  NONE: "NONE",
  FETCHING: "FETCHING",
  READY: "READY",
  RATED: "RATED",
  EXPIRED: "EXPIRED",
  ERROR: "ERROR",
};

const CustomerSatisfactionSurvey = (props: Props) => {
  const mounted = useRef(true);
  const { match: { params }, makeToast } = props;  
  const [status, setStatus] = useState(STATUS.NONE);

  const COMMENTS_MAXLENGTH = 3500;

  const starRatedColorConst ="#E03D4C";
  const starHoverColorConst ="#CA2030";    

  const [surveyResponse, setSurveyResponse] = useState<CustomerSatisfactionSurveyRequest>({
    workItemId: params.uId,
    satisfactionWithSchedulingProcess: 0,
    didInspectorArriveOnTime: 0,
    inspectorsProfessionalismAndCommunication: 0,
    overallSatisfaction: 0,
    howCanWeImproveTheExperience: ""
  });

  useEffect(() => {
    if(status === STATUS.NONE)
      fetchStatus();

    return () => { mounted.current = false; };
  }, []);

  const fetchStatus = () => {      
    isSatisfactionSurveyAvailable(
      params.uId, 
      (data) => { data && data.canCompleteSatisfactionSurvey ? setStatus(STATUS.READY) : setStatus(STATUS.EXPIRED); },
      () => { setStatus(STATUS.ERROR); },
      mounted
    );    
    setStatus(STATUS.FETCHING);
  };

  const updateSurveyResponse = (key : string, val : any) => {
    setSurveyResponse((prevSurveyResponse) => ({
      ...prevSurveyResponse,
      [key]: val
    }));
  }
  
  const submitFeedback = () => {  
    let passesValidation = true;
    let responseToSubmit = {...surveyResponse};    
    responseToSubmit.workItemId = params.uId;
    Object.keys(responseToSubmit).forEach((key) => {
      if (responseToSubmit[key] === null || responseToSubmit[key] === 0) {
        passesValidation = false;
      }
    });

    if(!passesValidation){  
      makeToast("Please fill out each question before submitting.");
      return;
    }
    
    setSatisfactionSurvey(params.uId, responseToSubmit, () => { 
      setStatus(STATUS.RATED); 
    }, () => setStatus(STATUS.READY), mounted);    
    setStatus(STATUS.FETCHING);
  };

  return (
    <div className="satisfaction-survey-root">
      {(status === STATUS.NONE || status === STATUS.FETCHING) && <MSpinner />}
      {status === STATUS.READY &&
        <>        
          <h3>Your feedback is important to us!</h3>
          <div className="rating-tip">(1 - Not at all satisfied, 2 - Somewhat dissatisfied, 3 - Neither dissatisfied or satisfied, 4 - Somewhat satisfied, 5 - Extremely satisfied)</div>                 
          <h4>How satisfied are you with the process of scheduling your appointment?</h4>   
          <StarRatings 
            rating={surveyResponse.satisfactionWithSchedulingProcess}
            changeRating={(nextRating) => updateSurveyResponse('satisfactionWithSchedulingProcess', nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}  
          /> 
          <h4>How satisfied are you with the timeliness of our field representative's arrival for your scheduled appointment?</h4> 
          <StarRatings 
            rating={surveyResponse.didInspectorArriveOnTime}
            changeRating={(nextRating) => updateSurveyResponse('didInspectorArriveOnTime', nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}  
          />                    
          <h4>How would you rate the field representatives professionalism and communication?</h4>  
          <StarRatings 
            rating={surveyResponse.inspectorsProfessionalismAndCommunication}
            changeRating={(nextRating) => updateSurveyResponse('inspectorsProfessionalismAndCommunication', nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />  
          <h4>Overall, how satisfied are you with your experience with Mueller?</h4>       
          <StarRatings 
            rating={surveyResponse.overallSatisfaction}
            changeRating={(nextRating) => updateSurveyResponse('overallSatisfaction', nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />           
          <h4>What could Mueller do to improve the experience? (optional):</h4>                              
          <TextField
            variant="standard"
            id="rating-comments"
            label=""
            inputProps={{ maxLength: COMMENTS_MAXLENGTH }}
            multiline
            rows={3}
            onChange={(e) => updateSurveyResponse('howCanWeImproveTheExperience', e.target.value)}
          />
          <h3>Thank you for your time!</h3>
          <Button onClick={submitFeedback}>Submit</Button>
        </>
      }
      {status === STATUS.RATED &&
        <div className="satisfaction-survey-complete">
          <h3>Thank you for your feedback!</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.EXPIRED &&
        <div className="satisfaction-survey-complete">
          <h3>This link has expired.  Please contact customer service with any additional feedback.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.ERROR &&
        <div className="satisfaction-survey-complete">
          <h3>Oops!  You appear to have reached this page in error.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button>Contact Us</Button>
          </a>
        </div>
      }
    </div>  
  );
};

const mapDispatchToProps = dispatch => ({
  makeToast: text => {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(null, mapDispatchToProps)(CustomerSatisfactionSurvey);